/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AdminApi, ConfigApi, SassApi } from '../apis';
import { ConfigContext } from '../contexts';
import { EmpDepartment, IUnitWithDisplayName } from '../../config/interfaces';

/**
 * Redux slice for managing configuration-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to application configuration, such as:
 * - Managing units
 * - Selecting and checking units (all or favorite)
 * - Handling SAS token retrieval for secure operations
 *
 * @property {object} initialState - The initial state for the configuration context.
 * @property {Function} setSelectedUnits - Sets the selected units in state.
 * @property {Function} setAllUnitsChecked - Sets the "all units checked" flag and marks units as selected.
 * @property {Function} setFavUnitsChecked - Sets the "favorite units checked" flag and marks units as selected.
 *
 * @example
 * dispatch(setSelectedUnits([{ unitId: 'A.3N', displayName: '3 North' }]));
 * dispatch(setAllUnitsChecked(true));
 * dispatch(setFavUnitsChecked(false));
 *
 * @returns {object} The config slice reducer and actions.
 */
const configSlice = createSlice({
  name: 'config',
  initialState: ConfigContext,
  reducers: {
    setSelectedUnits: (
      state,
      action: PayloadAction<IUnitWithDisplayName[]>
    ) => {
      return {
        ...state,
        selectedUnits: [...action.payload]
      };
    },
    setFavUnitsChecked: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isFavUnitsChecked: action.payload,
        unitsSelected: true
      };
    },
    setAllUnitsChecked: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isAllUnitsChecked: action.payload,
        unitsSelected: true
      };
    },
    setEmpSelectedDepts: (state, action: PayloadAction<EmpDepartment[]>) => {
      return {
        ...state,
        selectedEmpDepartments: action.payload
      };
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      AdminApi.endpoints.getVersion.matchFulfilled,
      (state, action) => ({
        ...state,
        apiVersion: action.payload
      })
    );
    builder.addMatcher(
      ConfigApi.endpoints.getFacilities.matchFulfilled,
      (state, action) => ({
        ...state,
        facilities: action.payload.facilities,
        userAccessibleFacilities: action.payload.userAccessibleFacilities
      })
    );
    builder.addMatcher(
      AdminApi.endpoints.getEmpDepartments.matchFulfilled,
      (state, action) => ({
        ...state,
        empDepartments: action.payload.departments
      })
    );
    builder.addMatcher(
      ConfigApi.endpoints.getUnits.matchFulfilled,
      (state, action) => ({
        ...state,
        units: action.payload
      })
    );
    builder.addMatcher(
      SassApi.endpoints.getSassToken.matchFulfilled,
      (state, action) => ({
        ...state,
        tokenExpiry: action.payload.tokenExpirationDateInUTC
      })
    );
  }
});

export const {
  setSelectedUnits,
  setFavUnitsChecked,
  setAllUnitsChecked,
  setEmpSelectedDepts
} = configSlice.actions;

export default configSlice.reducer;
